(function () {
  'use strict';

  var PAYLOAD_PARAM = 'line_order_payload';
  var READY_TIMEOUT_MS = 40000;
  var SUBMIT_DELAY_MS = 500;
  var CART_WAIT_TIMEOUT_MS = 20000;
  var CART_WAIT_INTERVAL_MS = 500;
  var CART_SETTLE_MS = 1500;
  var SESSION_KEY_PREFIX = 'natsu-line-order-autofill:';

  function init() {
    var payload = readPayload();
    if (!payload) return;
    var currentItem = getCurrentPayloadItem(payload);

    waitForReady(currentItem)
      .then(function (form) {
        return applyPayload(form, currentItem).then(function () {
          return submitPayload(form, payload);
        });
      })
      .catch(function (error) {
        console.error('[line-order-autofill]', error);
        showBanner('error', buildErrorMessage(error));
      });
  }

  function readPayload() {
    var encoded = new URLSearchParams(window.location.search).get(PAYLOAD_PARAM);
    if (!encoded) return null;

    try {
      var normalized = encoded.replace(/-/g, '+').replace(/_/g, '/');
      var padded = normalized + '==='.slice((normalized.length + 3) % 4);
      var binary = atob(padded);
      var bytes = Uint8Array.from(binary, function (char) {
        return char.charCodeAt(0);
      });
      var json = new TextDecoder('utf-8').decode(bytes);
      return JSON.parse(json);
    } catch (error) {
      console.error('[line-order-autofill] invalid payload', error);
      return null;
    }
  }

  function encodePayload(payload) {
    var json = JSON.stringify(payload);
    var bytes = new TextEncoder().encode(json);
    var binary = Array.prototype.map.call(bytes, function (byte) {
      return String.fromCharCode(byte);
    }).join('');
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
  }

  function getPayloadItems(payload) {
    if (Array.isArray(payload.items) && payload.items.length) {
      return payload.items.map(function (item) {
        return {
          h: item.h,
          v: item.v,
          q: item.q,
          p: item.p || {},
        };
      });
    }

    return [{
      h: payload.h,
      v: payload.v,
      q: payload.q,
      p: payload.p || {},
    }];
  }

  function getCurrentPayloadIndex(payload) {
    var items = getPayloadItems(payload);
    var index = Number(payload.i || 0);
    if (!Number.isFinite(index) || index < 0) return 0;
    if (index >= items.length) return items.length - 1;
    return index;
  }

  function getCurrentPayloadItem(payload) {
    var items = getPayloadItems(payload);
    return items[getCurrentPayloadIndex(payload)] || items[0];
  }

  function waitForReady(payload) {
    var startedAt = Date.now();
    return new Promise(function (resolve, reject) {
      function check() {
        var form = findProductForm();
        if (form && isReady(form, payload)) {
          resolve(form);
          return;
        }

        if (Date.now() - startedAt > READY_TIMEOUT_MS) {
          var missing = form ? getRequiredPropertyNames(payload.p || {}).filter(function (name) {
            return getPropertyFields(name).length === 0;
          }) : [];
          reject(new Error(
            missing.length
              ? 'timed out waiting for product form: ' + missing.join(', ')
              : 'timed out waiting for product form'
          ));
          return;
        }

        setTimeout(check, 250);
      }

      check();
    });
  }

  function isReady(form, payload) {
    if (!form.querySelector('[name="id"]')) {
      return false;
    }

    var submitButton = findSubmitButton(form);
    if (!submitButton) {
      return false;
    }

    var propertyNames = getRequiredPropertyNames(payload.p || {});

    if (!propertyNames.length) {
      return true;
    }

    return propertyNames.every(function (name) {
      return getPropertyFields(name).length > 0 || isOptionalField(name);
    });
  }

  function isOptionalField(name) {
    return name === '備考欄'
      || name === '入力欄'
      || name === '仕上がり希望'
      || name === 'SNS掲載許可'
      || name === 'ご協力ありがとうございます。よろしければご使用カメラ、SNSアカウント名、ご使用フィルムをご記入ください。';
  }

  function isRequiredField(name) {
    return name === 'プラン選択'
      || name === 'データ形式'
      || name === '納期オプション'
      || name === 'オプション';
  }

  function hasMeaningfulValue(value) {
    return String(value || '').trim() !== '';
  }

  function getVisiblePropertyEntries(properties) {
    return Object.entries(properties).filter(function (entry) {
      return entry[0].charAt(0) !== '_' && hasMeaningfulValue(entry[1]);
    });
  }

  function getRequiredPropertyNames(properties) {
    return getVisiblePropertyEntries(properties)
      .map(function (entry) { return entry[0]; })
      .filter(function (name) {
        return isRequiredField(name) && !isOptionalField(name);
      });
  }

  function applyPayload(form, payload) {
    showBanner('loading', 'LINEの注文内容を反映しています...');

    return setQuantity(form, payload.q)
      .then(function () {
        return applyProperties(form, payload.p || {});
      })
      .then(function () {
        ensureHiddenProperties(form, payload.p || {});
      })
      .then(function () {
        if (payload.v) {
          var variantInput = form.querySelector('[name="id"]');
          if (variantInput) {
            variantInput.value = payload.v;
          }
        }
      });
  }

  function setQuantity(form, quantity) {
    return new Promise(function (resolve) {
      var input = form.querySelector('[name="quantity"]');
      if (!input || !quantity) {
        resolve();
        return;
      }

      input.value = String(quantity);
      dispatchInputEvents(input);
      resolve();
    });
  }

  function applyProperties(form, properties) {
    var entries = getVisiblePropertyEntries(properties);
    return entries.reduce(function (chain, entry) {
      return chain.then(function () {
        return applyProperty(form, entry[0], entry[1]);
      });
    }, Promise.resolve());
  }

  function applyProperty(form, name, value) {
    if (name.charAt(0) === '_') {
      return Promise.resolve();
    }

    var fields = getPropertyFields(name);
    if (!fields.length) {
      return Promise.resolve();
    }

    var firstField = fields[0];

    if (firstField.tagName === 'TEXTAREA' || firstField.type === 'text') {
      setFieldValue(firstField, value);
      return Promise.resolve();
    }

    if (firstField.type === 'radio') {
      return selectSingleValue(fields, value);
    }

    if (firstField.type === 'checkbox') {
      return selectMultipleValues(fields, splitOptionValues(value));
    }

    return Promise.resolve();
  }

  function selectSingleValue(fields, value) {
    var match = findMatchingField(fields, value);
    if (!match) {
      return Promise.resolve();
    }

    if (!match.checked) {
      match.click();
      match.checked = true;
      match.dispatchEvent(new Event('change', { bubbles: true }));
    }

    return wait(SUBMIT_DELAY_MS);
  }

  function selectMultipleValues(fields, values) {
    values.forEach(function (value) {
      var match = findMatchingField(fields, value);
      if (!match) return;
      if (!match.checked) {
        match.click();
        match.checked = true;
        match.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    return wait(SUBMIT_DELAY_MS);
  }

  function findMatchingField(fields, wantedValue) {
    var candidates = buildValueCandidates(wantedValue);
    return fields.find(function (field) {
      return candidates.indexOf(normalizeValue(field.value)) !== -1;
    }) || null;
  }

  function buildValueCandidates(value) {
    var normalized = normalizeValue(value);
    if (normalized === 'TIFF(16bit)') {
      return ['TIFF(16bit)', 'TIFF'];
    }
    if (normalized === 'TIFF') {
      return ['TIFF', 'TIFF(16bit)'];
    }
    return [normalized];
  }

  function normalizeValue(value) {
    return String(value || '').replace(/\s+/g, ' ').trim();
  }

  function splitOptionValues(value) {
    return String(value || '')
      .split(/\s*\/\s*/)
      .map(function (item) { return item.trim(); })
      .filter(Boolean);
  }

  function setFieldValue(field, value) {
    field.value = String(value || '');
    dispatchInputEvents(field);
  }

  function dispatchInputEvents(field) {
    field.dispatchEvent(new Event('input', { bubbles: true }));
    field.dispatchEvent(new Event('change', { bubbles: true }));
    field.dispatchEvent(new Event('blur', { bubbles: true }));
  }

  function ensureHiddenProperties(form, properties) {
    Object.entries(properties).forEach(function (entry) {
      var name = entry[0];
      var value = String(entry[1] || '');
      if (!value && name.charAt(0) !== '_') return;
      if (getPropertyFields(name).length) return;

      var hidden = form.querySelector('input[name="properties[' + cssEscape(name) + ']"]');
      if (!hidden) {
        hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = 'properties[' + name + ']';
        form.appendChild(hidden);
      }
      hidden.value = value;
    });
  }

  function submitPayload(form, payload) {
    var items = getPayloadItems(payload);
    var itemIndex = getCurrentPayloadIndex(payload);
    var currentItem = items[itemIndex];
    var sessionId = getItemSessionId(payload, currentItem, itemIndex);
    var sessionKey = SESSION_KEY_PREFIX + sessionId;

    return fetchCartState()
      .catch(function () {
        return { item_count: 0, items: [] };
      })
      .then(function (initialCart) {
        var previousState = sessionStorage.getItem(sessionKey);
        if (previousState === 'done' || previousState === 'pending') {
          if (hasSessionItem(initialCart, sessionId)) {
            showBanner('success', 'LINEの注文内容はすでに反映済みです。移動します...');
            return proceedAfterItem(payload, items, itemIndex);
          }

          sessionStorage.removeItem(sessionKey);
        }

        sessionStorage.setItem(sessionKey, 'pending');
        showBanner('loading', 'カートへ追加しています...');
        return wait(SUBMIT_DELAY_MS).then(function () {
          submitThroughStorefront(form);
          return waitForCartAdd(sessionId, initialCart.item_count || 0);
        });
      })
      .then(function () {
        sessionStorage.setItem(sessionKey, 'done');
        return proceedAfterItem(payload, items, itemIndex);
      })
      .catch(function (error) {
        sessionStorage.removeItem(sessionKey);
        throw error;
      });
  }

  function getItemSessionId(payload, item, itemIndex) {
    if (item && item.p && item.p._line_session_id) {
      return item.p._line_session_id;
    }

    var orderId = payload.a && payload.a['LINE注文ID'] ? payload.a['LINE注文ID'] : location.href;
    return getPayloadItems(payload).length > 1 ? orderId + '-' + (itemIndex + 1) : orderId;
  }

  function proceedAfterItem(payload, items, itemIndex) {
    if (itemIndex < items.length - 1) {
      redirectToNextItem(payload, items[itemIndex + 1], itemIndex + 1);
      return Promise.resolve();
    }

    return updateCart(payload.n, payload.a || {}).then(function () {
      redirectToDestination(payload.d);
    });
  }

  function redirectToNextItem(payload, nextItem, nextIndex) {
    if (!nextItem || !nextItem.h) {
      redirectToDestination(payload.d);
      return;
    }

    var nextPayload = Object.assign({}, payload, { i: nextIndex });
    var searchParams = new URLSearchParams(window.location.search);
    var url = new URL('/products/' + nextItem.h, window.location.origin);

    ['preview_theme_id', '_fd', 'pb'].forEach(function (name) {
      var value = searchParams.get(name);
      if (value) {
        url.searchParams.set(name, value);
      }
    });

    url.searchParams.set(PAYLOAD_PARAM, encodePayload(nextPayload));
    showBanner('success', '明細' + nextIndex + 'を反映しました。次の商品へ移動します...');
    window.location.assign(url.toString());
  }

  function submitThroughStorefront(form) {
    var submitButton = findSubmitButton(form);
    if (submitButton) {
      submitButton.click();
      return;
    }

    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
      return;
    }

    form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
  }

  function waitForCartAdd(sessionId, initialItemCount) {
    var startedAt = Date.now();
    return new Promise(function (resolve, reject) {
      function check() {
        fetchCartState()
          .then(function (cart) {
            if (hasSessionItem(cart, sessionId) || isCartAdvanced(cart, initialItemCount) || hasErrorMessage()) {
              if (hasErrorMessage()) {
                reject(new Error(readErrorMessage() || 'product form rejected submission'));
                return;
              }
              wait(CART_SETTLE_MS).then(function () {
                fetchCartState()
                  .then(resolve)
                  .catch(function () {
                    resolve(cart);
                  });
              });
              return;
            }

            if (Date.now() - startedAt > CART_WAIT_TIMEOUT_MS) {
              reject(new Error('timed out waiting for cart add'));
              return;
            }

            setTimeout(check, CART_WAIT_INTERVAL_MS);
          })
          .catch(function () {
            if (Date.now() - startedAt > CART_WAIT_TIMEOUT_MS) {
              reject(new Error('timed out waiting for cart add'));
              return;
            }
            setTimeout(check, CART_WAIT_INTERVAL_MS);
          });
      }

      check();
    });
  }

  function fetchCartState() {
    return fetch('/cart.js', {
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    }).then(function (response) {
      if (!response.ok) {
        throw new Error('cart fetch failed');
      }
      return response.json();
    });
  }

  function hasSessionItem(cart, sessionId) {
    return Array.isArray(cart && cart.items) && cart.items.some(function (item) {
      return item && item.properties && item.properties._line_session_id === sessionId;
    });
  }

  function isCartAdvanced(cart, initialItemCount) {
    return Number(cart && cart.item_count) > Number(initialItemCount || 0);
  }

  function hasErrorMessage() {
    var wrapper = document.querySelector('.product-form__error-message-wrapper');
    if (!wrapper || wrapper.hasAttribute('hidden')) {
      return false;
    }

    var message = wrapper.querySelector('.product-form__error-message');
    return !!(message && message.textContent && message.textContent.trim());
  }

  function readErrorMessage() {
    var message = document.querySelector('.product-form__error-message');
    return message ? message.textContent.trim() : '';
  }

  function updateCart(note, attributes) {
    var params = new URLSearchParams();
    if (note) {
      params.set('note', note);
    }

    Object.keys(attributes || {}).forEach(function (key) {
      params.set('attributes[' + key + ']', attributes[key]);
    });

    if (!params.toString()) {
      return Promise.resolve();
    }

    return fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: params.toString(),
    }).then(function (response) {
      if (!response.ok) {
        throw new Error('cart update failed');
      }
    });
  }

  function redirectToDestination(destination) {
    var searchParams = new URLSearchParams(window.location.search);
    var isPreview = !!searchParams.get('preview_theme_id');
    var nextDestination = isPreview ? 'cart' : destination;
    var target = nextDestination === 'cart' ? '/cart' : '/checkout';
    var url = new URL(target, window.location.origin);
    ['preview_theme_id', '_fd', 'pb'].forEach(function (name) {
      var value = searchParams.get(name);
      if (value && nextDestination === 'cart') {
        url.searchParams.set(name, value);
      }
    });

    showBanner('success', '内容を反映しました。移動します...');
    window.location.assign(url.toString());
  }

  function getPropertyFields(label) {
    return Array.prototype.filter.call(document.querySelectorAll('[name]'), function (field) {
      return field && (
        field.name === 'properties[' + label + ']'
        || field.name === 'properties[' + label + '][]'
      );
    });
  }

  function findProductForm() {
    return document.querySelector('product-form form[action*="/cart/add"]')
      || document.querySelector('form[action*="/cart/add"]');
  }

  function findSubmitButton(form) {
    return form.querySelector('button[name="add"][type="submit"]')
      || form.querySelector('button[type="submit"]');
  }

  function wait(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  var bannerElement = null;

  function showBanner(type, message) {
    if (!bannerElement) {
      bannerElement = document.createElement('div');
      bannerElement.id = 'natsu-line-order-banner';
      bannerElement.style.position = 'fixed';
      bannerElement.style.right = '16px';
      bannerElement.style.bottom = '16px';
      bannerElement.style.zIndex = '999999';
      bannerElement.style.maxWidth = '360px';
      bannerElement.style.padding = '14px 18px';
      bannerElement.style.borderRadius = '10px';
      bannerElement.style.boxShadow = '0 12px 30px rgba(0,0,0,0.16)';
      bannerElement.style.fontSize = '14px';
      bannerElement.style.lineHeight = '1.6';
      bannerElement.style.fontFamily = 'Hiragino Sans, Yu Gothic, sans-serif';
      document.body.appendChild(bannerElement);
    }

    var palette = {
      loading: { background: '#f4eadf', color: '#5b4a42' },
      success: { background: '#f2f0e9', color: '#3d4c3f' },
      error: { background: '#f8e8e2', color: '#8a3e31' },
    };
    var current = palette[type] || palette.loading;
    bannerElement.style.background = current.background;
    bannerElement.style.color = current.color;
    bannerElement.textContent = message;
  }

  function buildErrorMessage(error) {
    var message = String(error && error.message ? error.message : '');
    if (message.indexOf('timed out waiting for product form') !== -1) {
      var details = message.split(': ').slice(1).join(': ');
      return details
        ? '注文項目の読み込み待ちで時間切れになりました。見つからない項目: ' + details
        : '注文項目の読み込み待ちで時間切れになりました。ページを再読み込みして、もう一度お試しください。';
    }
    if (message.indexOf('product form rejected submission') !== -1) {
      return '必須項目の反映に失敗しました。ページを再読み込みして、もう一度お試しください。';
    }
    if (message.indexOf('cart add failed') !== -1) {
      return 'カート追加に失敗しました。ページを再読み込みして、もう一度お試しください。';
    }
    return 'LINEの注文内容を反映できませんでした。ページを再読み込みしてから、スタッフへご連絡ください。';
  }

  function cssEscape(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(value);
    }
    return String(value).replace(/["\\]/g, '\\$&');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
